/**
 * MarketingMobileMenu — full-screen mobile navigation overlay for the
 * marketing header. Deliberately boring: iOS WebKit desyncs composited
 * layers while scrolling inside a fixed overlay and mispaints it (Radix
 * Sheet and plain scrolling overlays both corrupt on device).
 *
 * The load-bearing invariant: THE MENU MUST NEVER NEED TO SCROLL in
 * portrait on the smallest supported iPhone. Content budget ≈ 456pt
 * (header row ~72 + 3 CTAs ~188 + 3 links ~196) against a ~548pt small
 * viewport (iPhone SE). If the corrupted scroll path never runs, it
 * cannot mispaint. Do NOT add nav items or vertical padding here without
 * re-checking that budget; the full site map lives in the desktop nav
 * and the footer.
 *
 * Secondary defenses:
 * - Rendered IN PLACE (no portal) so [data-theme="marketing"] applies.
 * - No transform animations, no position: sticky, no nested scrollers.
 * - overflow-y: auto stays ONLY as a landscape/tiny-viewport fallback.
 * - height: 100dvh tracks the iOS dynamic toolbar (browserslist floor
 *   is Safari/iOS 16.4).
 * - Body scroll lock is two style writes, not a library.
 * - Critical layout properties are inline styles (CSS chunk-ordering
 *   resilience, same convention as marketing-header.tsx).
 */

'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';

import { MktgButton } from '@/components/marketing/mktg-button';

interface MobileNavItem {
  href: string;
  label: string;
}

interface MarketingMobileMenuProps {
  open: boolean;
  onClose: () => void;
  /** Show Log in / Sign Up / Book a Demo (unauthenticated visitors only) */
  showCtas: boolean;
  logoSrc: string;
  /** Logo renders only after mount (client-resolved asset, mirrors header) */
  showLogo: boolean;
  /**
   * Max 3 items — the no-scroll content budget documented in the file
   * header. Everything else stays reachable via desktop nav + footer.
   */
  navItems: MobileNavItem[];
}

export function MarketingMobileMenu({
  open,
  onClose,
  showCtas,
  logoSrc,
  showLogo,
  navItems
}: MarketingMobileMenuProps) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  // While open: lock page scroll behind the overlay and close on Escape.
  useEffect(() => {
    if (!open) return;

    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    closeButtonRef.current?.focus();

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role='dialog'
      aria-modal='true'
      aria-label='Navigation menu'
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '100dvh',
        zIndex: 100,
        backgroundColor: 'var(--mktg-white, #ffffff)',
        color: 'var(--mktg-dark, #1e1e1e)',
        overflowY: 'auto',
        overscrollBehavior: 'contain'
      }}
    >
      {/* Top row: logo + close. Static (not sticky) — the menu fits the
          viewport, so nothing ever scrolls past it. */}
      <div
        className='px-6 pt-5 pb-3'
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'var(--mktg-white, #ffffff)',
          borderBottom: '1px solid var(--mktg-card-border, #f0f0f0)'
        }}
      >
        <div>
          {showLogo && (
            <Image
              src={logoSrc}
              alt='TEX Edu'
              width={100}
              height={40}
              className='object-contain'
            />
          )}
        </div>
        <button
          ref={closeButtonRef}
          type='button'
          onClick={onClose}
          aria-label='Close navigation menu'
          style={{
            background: 'none',
            border: '1px solid var(--mktg-card-border, #f0f0f0)',
            borderRadius: '8px',
            padding: '6px',
            minWidth: '44px',
            minHeight: '44px',
            cursor: 'pointer',
            color: 'var(--mktg-dark, #1e1e1e)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <line x1='18' y1='6' x2='6' y2='18' />
            <line x1='6' y1='6' x2='18' y2='18' />
          </svg>
        </button>
      </div>

      {showCtas && (
        <div className='flex flex-col gap-2 px-6 pt-5 pb-5'>
          <MktgButton
            variant='secondary'
            size='sm'
            href='/auth/login'
            onClick={onClose}
            className='min-h-[44px] w-full'
          >
            Log in
          </MktgButton>
          <MktgButton
            variant='secondary'
            size='sm'
            href='/auth/register'
            onClick={onClose}
            className='min-h-[44px] w-full'
          >
            Sign Up
          </MktgButton>
          <MktgButton
            variant='primary'
            size='sm'
            href='/request-demo'
            onClick={onClose}
            className='min-h-[44px] w-full'
          >
            Book a Demo
          </MktgButton>
        </div>
      )}

      <nav
        aria-label='Mobile navigation'
        className='flex flex-col gap-1 px-6 pt-5 pb-8'
        style={{
          borderTop: showCtas
            ? '1px solid var(--mktg-card-border, #f0f0f0)'
            : undefined
        }}
      >
        {navItems.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            onClick={onClose}
            className='rounded-lg text-base font-medium transition-colors hover:bg-gray-50'
            style={{
              color: 'var(--mktg-dark, #1e1e1e)',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {item.label}
          </Link>
        ))}
      </nav>
    </div>
  );
}
